import { CORE, core, getToken } from "../../lib/api";

export type TemplateButton = { type: "QUICK_REPLY" | "URL" | "PHONE_NUMBER" | "TEMPLATE"; text: string; value?: string };
export type CarouselCard = {
  header_media_url: string;
  header_format: "IMAGE" | "VIDEO";
  body: string;
  buttons: TemplateButton[];
};
export type WhatsAppTemplate = {
  template_id: string;
  name: string;
  type: "TEXT" | "IMAGE" | "VIDEO";
  category: "MARKETING" | "UTILITY" | "AUTHENTICATION";
  language: string;
  body: string;
  variables: number[];
  header_media_url?: string;
  header_media_urls?: string[];
  buttons: TemplateButton[];
  template_kind?: "normal" | "carousel";
  cards?: CarouselCard[];
  status: string;
  /** Meta review detail, once submitted (see submit/sync below). */
  meta_status?: string;
  meta_template_id?: string;
  meta_rejection_reason?: string;
  meta_converted_buttons?: string[];
  submitted_instance_name?: string;
  submitted_at?: string;
  last_synced_at?: string;
  /** Present on sync responses; False means Meta has no such template yet. */
  meta_found?: boolean;
  created_at?: string;
  updated_at?: string;
};

export type TemplateDraft = {
  name: string;
  type: "TEXT" | "IMAGE" | "VIDEO";
  category: "MARKETING" | "UTILITY" | "AUTHENTICATION";
  language: string;
  body: string;
  header_media_url: string;
  header_media_urls: string[];
  buttons: TemplateButton[];
  template_kind: "normal" | "carousel";
  cards: CarouselCard[];
};

export const emptyDraft = (kind: "normal" | "carousel" = "normal"): TemplateDraft => ({
  name: "",
  type: "TEXT",
  category: kind === "carousel" ? "MARKETING" : "MARKETING",
  language: "en",
  body: "",
  header_media_url: "",
  header_media_urls: [],
  buttons: [],
  template_kind: kind,
  cards: kind === "carousel" ? [
    { header_media_url: "", header_format: "IMAGE", body: "", buttons: [] },
    { header_media_url: "", header_format: "IMAGE", body: "", buttons: [] },
  ] : [],
});

export const emptyCard = (): CarouselCard => ({
  header_media_url: "", header_format: "IMAGE", body: "", buttons: [],
});

export const templateVariables = (body: string): number[] => {
  const found = new Set<number>();
  for (const m of (body || "").matchAll(/\{\{(\d+)\}\}/g)) found.add(parseInt(m[1], 10));
  return [...found].sort((a, b) => a - b);
};

export type WhatsAppCampaignCreate = {
  batch_id: string;
  message_kind: "template" | "ai_agent";
  template_id?: string;
  agent_id?: string;
  instance_name: string;
  mode: "now" | "later";
  scheduled_at?: string;
  timezone?: string;
  concurrency?: number;
  campaign?: string;
};

export const templatesApi = {
  list: () => core<WhatsAppTemplate[]>(`/api/v1/whatsapp-templates`),
  create: (payload: TemplateDraft) =>
    core<WhatsAppTemplate>(`/api/v1/whatsapp-templates`, { method: "POST", body: JSON.stringify(payload) }),
  update: (id: string, payload: Partial<TemplateDraft>) =>
    core<WhatsAppTemplate>(`/api/v1/whatsapp-templates/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(payload) }),
  remove: (id: string) =>
    core<void>(`/api/v1/whatsapp-templates/${encodeURIComponent(id)}`, { method: "DELETE" }),
  /** Submit for Meta review under a Cloud-API number. Goes PENDING; approval
   *  itself arrives later via `sync`. `accessToken` (System-User permanent
   *  token, used once, never stored) is only needed for IMAGE/VIDEO headers. */
  submit: (id: string, payload: { instance_name: string; access_token?: string }) =>
    core<WhatsAppTemplate>(`/api/v1/whatsapp-templates/${encodeURIComponent(id)}/submit`, { method: "POST", body: JSON.stringify(payload) }),
  /** Poll Meta for the review status and fold it onto the row. */
  sync: (id: string, payload?: { instance_name?: string }) =>
    core<WhatsAppTemplate>(`/api/v1/whatsapp-templates/${encodeURIComponent(id)}/sync`, { method: "POST", body: JSON.stringify(payload ?? {}) }),
  uploadHeaderImage: async (file: File): Promise<{ url: string }> => {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${CORE}/api/v1/whatsapp-templates/media`, {
      method: "POST",
      headers: { ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
      body: form,
    });
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        detail = typeof body?.detail === "string" ? body.detail : JSON.stringify(body?.detail ?? body);
      } catch { /* keep HTTP fallback */ }
      throw new Error(detail);
    }
    return res.json();
  },
  resolveMediaUrl: (url: string) =>
    core<{ url: string }>(`/api/v1/whatsapp-templates/media-url`, { method: "POST", body: JSON.stringify({ url }) }),
};

export const campaignsApi = {
  list: (): Promise<any[]> => core<any[]>(`/api/v1/whatsapp-campaigns`),
  create: (payload: WhatsAppCampaignCreate) =>
    core<any>(`/api/v1/whatsapp-campaigns`, { method: "POST", body: JSON.stringify(payload) }),
  get: (id: string): Promise<any> => core<any>(`/api/v1/whatsapp-campaigns/${encodeURIComponent(id)}`),
  recipients: (id: string, page = 1): Promise<{ items: any[]; page: number; page_size: number; total: number }> =>
    core<{ items: any[]; page: number; page_size: number; total: number }>(
      `/api/v1/whatsapp-campaigns/${encodeURIComponent(id)}/recipients?page=${page}`,
    ),
};
